/**
 *
 * @typedef {Object} StoreFile
 * @property {string} id - The waybill number.
 * @property {string} category - The category of the file.
 * @property {File} data - The data of the file.
 * @property {File} dataURL - The data of the file.
 * @property {boolean} flipped
 * @property {number} rotate
 * @property {boolean} upload_later
 * @property {boolean} uploaded
 * @property {boolean} will_not_upload
 */

import { IMPRESSION_TYPE } from '../../hooks/DiffRetainerHooks/useRetainerRequestForm';

export const StoreFile = {};

/**
 *
 * @typedef {Object} StoreFilesValue
 * @property {string} scannerBrand - The brand of the scanner.
 * @property {string} doctorId - The ID of the doctor.
 * @property {string} modernCode - The modern code.
 * @property {[StoreFile]} files - An array of files, filtered to only include those with a category of 'stl_file'.
 * @property {string} waybillNumber - The waybill number.
 * @property {string} courierCompany - The name of the courier company.
 */

export const StoreFilesValue = {};

/**
 * @typedef {Object} CaseImpressionFileType
 * @property {string} FILE - Represents a file case impression type.
 * @property {string} COURIER - Represents a courier case impression type.
 * @property {string} SCANNER - Represents a scanner case impression type.
 * @property {string} LATEST_APPROVED_TREATMENT_PLAN - Represents a lastest approved treatment plan case impression type.
 * @property {string} LATEST_RETAINER_REQUEST - Represents using the latest retainer request case's impression files.
 */
export const CaseImpressionFileType = {
    FILE: 'FILE',
    COURIER: 'COURIER',
    SCANNER: 'SCANNER',
    LATEST_APPROVED_TREATMENT_PLAN: 'LATEST_APPROVED_TREATMENT_PLAN',
    LATEST_RETAINER_REQUEST: 'LATEST_RETAINER_REQUEST',
};

/**
 *
 * @typedef {Object} CaseImpression
 * @property {string} caseId - The ID of the case.
 * @property {string | null} stl_file0
 * @property {string | null} stl_file1
 * @property {string | null} stl_file2
 * @property {string | null} stl_file3
 * @property {string} modernCode - The modern code.
 * @property {string} waybillNumber - The waybill number.
 * @property {string} courierCompany - The name of the courier company.
 * @property {string} scannerBrand - The brand of the scanner.
 * @property {string} doctorId - The ID of the doctor.
 * @property {CaseImpressionFileType} type - The ID of the doctor.
 */

export const CaseImpression = {};
/**
 *
 * @typedef {Object} RetainerRequestFormValues
 * for upper type's value
 * @property {IMPRESSION_TYPE} impression_type - Question 1
 * @property {boolean} upperFixedWire - Question 2
 * @property {string | null} upperArchAndQuantity - Question 3
 * @property {string | null} upperBleachingRetainersSelect - Question 3's optional selection
 * @property {string | null} upperFixedPalatalWireSelect - Question 3's optional selection
 * @property {string | null} upperEcodontWireSelect - Question 3's optional selection
 * @property {string | null} upperOcclusalBiteRampQuestion - Question 4
 * @property {string | null} upperClearRetainerDesignUpper - Question 5A
 * @property {string} upperClearRetainerDesignUpperOption - Question 5A's option
 * @property {string} upperClearRetainerDesignLower - Question 5B
 * @property {string} upperClearRetainerDesignLowerOption - Question 5B's option
 * @property {string} upperClearRetainerDesignUpperForSecondSet - Question 6A
 * @property {string} upperClearRetainerDesignUpperOptionForSecondSet - Question 6A's option
 * @property {string} upperClearRetainerDesignLowerForSecondSet - Question 6B
 * @property {string} upperClearRetainerDesignLowerOptionForSecondSet - Question 6B's option
 * @property {string} upperPontics - Question 7
 * for lower type's value
 * @property {boolean} lowerFixedWire - Question 2
 * @property {string | null} lowerArchAndQuantity - Question 3
 * @property {string | null} lowerBleachingRetainersSelect - Question 3's optional selection
 * @property {string | null} lowerFixedPalatalWireSelect - Question 3's optional selection
 * @property {string | null} lowerEcodontWireSelect - Question 3's optional selection
 * @property {string | null} lowerOcclusalBiteRampQuestion - Question 4
 * @property {string | null} lowerClearRetainerDesignUpper - Question 5A
 * @property {string} lowerClearRetainerDesignUpperOption - Question 5A's option
 * @property {string} lowerClearRetainerDesignLower - Question 5B
 * @property {string} lowerClearRetainerDesignLowerOption - Question 5B's option
 * @property {string} lowerClearRetainerDesignUpperForSecondSet - Question 6A
 * @property {string} lowerClearRetainerDesignUpperOptionForSecondSet - Question 6A's option
 * @property {string} lowerClearRetainerDesignLowerForSecondSet - Question 6B
 * @property {string} lowerClearRetainerDesignLowerOptionForSecondSet - Question 6B's option
 * @property {string} lowerPontics - Question 7
 */

export const RetainerRequestFormValues = {};

/**
 *
 * @typedef {Object} CaseImpressionDTO
 * @property {string} case_id - The ID of the case.
 * @property {string | null} stl_file0
 * @property {string | null} stl_file1
 * @property {string | null} stl_file2
 * @property {string | null} stl_file3
 * @property {string} modern_code - The modern code.
 * @property {string} waybill_number - The waybill number.
 * @property {string} courier_company - The name of the courier company.
 * @property {string} scanner_brand - The brand of the scanner.
 * @property {string} doctor_id - The ID of the doctor.
 * @property {CaseImpressionFileType} type
 */

export const CaseImpressionDTO = {};
